import * as XLSX from 'xlsx';
import React, { useState, useRef, useCallback } from 'react';
import {
  X,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Info,
  Trash2,
  Download,
} from 'lucide-react';
import { ParseProgress, ParseResult } from './types';
import { ImportPreviewTable } from './ImportPreviewTable';

type ImportRow = {
  cnpjlojista: string;
  tamanho_ul: string;
};

type BatchImportResultItem = {
  index?: number;
  ok?: boolean;
  cnpjlojista?: string;
  tamanho_ul?: string;
  error?: string;
  action?: string;
  changed?: boolean;
  tamanho_ul_lojista_id?: number;
  before?: any;
  after?: any;
};

type BatchImportResponse = {
  success?: boolean;
  error?: string;
  count?: number;
  uniqueCount?: number;
  validLojistasCount?: number;
  notFoundCount?: number;
  notFoundCnpjs?: string[];
  changedCount?: number;
  historySaved?: boolean;
  results?: BatchImportResultItem[];
};

type ImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: (rows: ImportRow[]) => Promise<any> | any;
};

const IMPORT_BATCH_SIZE = 2500;

const normalizeDigits = (value: unknown): string => {
  return String(value ?? '').replace(/\D/g, '');
};

const normalizeText = (value: unknown): string => {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

const looksLikeBatchResponse = (value: any): boolean => {
  if (!value || typeof value !== 'object') return false;

  return [
    'success',
    'error',
    'count',
    'uniqueCount',
    'validLojistasCount',
    'notFoundCount',
    'notFoundCnpjs',
    'changedCount',
    'historySaved',
    'results',
  ].some((key) => key in value);
};

const extractBatchResponse = (raw: any): BatchImportResponse => {
  const candidates = [
    raw,
    raw?.data,
    raw?.response?.data,
    raw?.data?.data,
    raw?.response?.data?.data,
  ];

  const found = candidates.find(looksLikeBatchResponse);

  if (found) return found as BatchImportResponse;

  return {};
};

const isExplicitFailure = (response: BatchImportResponse): boolean => {
  return response?.success === false;
};

const isNotFoundResultItem = (item: BatchImportResultItem): boolean => {
  if (item?.ok !== false) return false;

  const errorText = normalizeText(item?.error);

  return (
    errorText.includes('cnpj não encontrado') ||
    errorText.includes('nao encontrado no sistema')
  );
};

export const ImportModal: React.FC<ImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete,
}) => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parseProgress, setParseProgress] = useState<ParseProgress | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importProgress, setImportProgress] = useState<{ sent: number; total: number } | null>(null);
  const [notFoundSummary, setNotFoundSummary] = useState<{ count: number; cnpjs: string[] }>({
    count: 0,
    cnpjs: [],
  });
  const [showNotFoundModal, setShowNotFoundModal] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const workerRef = useRef<Worker | null>(null);

  const terminateWorker = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    terminateWorker();
    setFile(null);
    setParseResult(null);
    setParseError(null);
    setParseProgress(null);
    setImporting(false);
    setImportDone(false);
    setImportErrors([]);
    setImportProgress(null);
    setNotFoundSummary({ count: 0, cnpjs: [] });
    setShowNotFoundModal(false);
  }, [terminateWorker]);

  React.useEffect(() => {
    if (isOpen) {
      resetState();
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen, resetState]);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !importing && !parseProgress && !showNotFoundModal) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose, importing, parseProgress, showNotFoundModal]);

  React.useEffect(() => {
    return () => terminateWorker();
  }, [terminateWorker]);

  const handleClose = () => {
    if (importing) return;
    resetState();
    onClose();
  };

  const handleFile = (f: File) => {
    setParseError(null);
    setImportDone(false);
    setImportErrors([]);
    setParseResult(null);
    setNotFoundSummary({ count: 0, cnpjs: [] });
    setShowNotFoundModal(false);
    terminateWorker();

    if (!f.name.endsWith('.xlsx') && !f.name.endsWith('.xls')) {
      setParseError('Formato inválido. Envie um arquivo .xlsx');
      return;
    }

    setFile(f);
    setParseProgress({ phase: 'reading', processed: 0, total: 0 });

    const reader = new FileReader();
    reader.onload = (e) => {
      const buffer = e.target?.result as ArrayBuffer;
      if (!buffer) {
        setParseError('Erro ao ler o arquivo.');
        setParseProgress(null);
        return;
      }

      const worker = new Worker(new URL('./xlsxWorker.ts', import.meta.url), {
        type: 'module',
      });
      workerRef.current = worker;

      worker.onmessage = (evt: MessageEvent) => {
        const msg = evt.data;

        if (msg.type === 'progress') {
          setParseProgress({
            phase: msg.phase,
            processed: msg.processed,
            total: msg.total,
          });
        } else if (msg.type === 'done') {
          setParseResult({
            totalRows: msg.totalRows,
            validCount: msg.validCount,
            invalidCount: msg.invalidCount,
            tamanhoSummary: msg.tamanhoSummary,
            errorSummary: msg.errorSummary,
            validItems: msg.validItems,
          });
          setParseProgress(null);
          terminateWorker();
        } else if (msg.type === 'error') {
          setParseError(msg.message);
          setParseProgress(null);
          terminateWorker();
        }
      };

      worker.onerror = () => {
        setParseError('Erro ao processar o arquivo. Tente novamente.');
        setParseProgress(null);
        terminateWorker();
      };

      worker.postMessage(buffer, [buffer]);
    };

    reader.onerror = () => {
      setParseError('Erro ao ler o arquivo.');
      setParseProgress(null);
    };

    reader.readAsArrayBuffer(f);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    if (inputRef.current) inputRef.current.value = '';
  };

  const downloadNotFoundSpreadsheet = () => {
  if (!notFoundSummary.cnpjs.length) return;

  const data = notFoundSummary.cnpjs.map((cnpj, index) => ({
    '#': index + 1,
    CNPJLojista: cnpj,
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Não Processados');

  const fileName = `cnpjs-nao-processados-${new Date()
    .toISOString()
    .slice(0, 19)
    .replace(/[:T]/g, '-')}.xlsx`;

  XLSX.writeFile(workbook, fileName);
};

  const handleImport = async () => {
    if (!parseResult || parseResult.validItems.length === 0) return;

    setImporting(true);
    setImportErrors([]);
    setNotFoundSummary({ count: 0, cnpjs: [] });
    setShowNotFoundModal(false);

    const items = parseResult.validItems;
    const totalBatches = Math.ceil(items.length / IMPORT_BATCH_SIZE);
    const errors: string[] = [];
    const notFoundSet = new Set<string>();
    let notFoundCountAccumulator = 0;

    setImportProgress({ sent: 0, total: items.length });

    for (let i = 0; i < totalBatches; i++) {
      const batch = items.slice(i * IMPORT_BATCH_SIZE, (i + 1) * IMPORT_BATCH_SIZE);

      try {
        const rawResponse = await onImportComplete(batch);
        const response = extractBatchResponse(rawResponse);

        if (typeof response.notFoundCount === 'number' && response.notFoundCount > 0) {
          notFoundCountAccumulator += response.notFoundCount;
        }

        if (Array.isArray(response.notFoundCnpjs)) {
          response.notFoundCnpjs.forEach((cnpj) => {
            const normalized = normalizeDigits(cnpj);
            if (normalized) notFoundSet.add(normalized);
          });
        }

        if (Array.isArray(response.results)) {
          response.results.forEach((item) => {
            if (isNotFoundResultItem(item)) {
              const normalized = normalizeDigits(item.cnpjlojista);
              if (normalized) notFoundSet.add(normalized);
            }
          });
        }

        if (isExplicitFailure(response)) {
          errors.push(response.error || `Erro no lote ${i + 1} de ${totalBatches}`);
        }
      } catch (err: any) {
        const message =
          err?.response?.data?.error ||
          err?.message ||
          `Erro no lote ${i + 1} de ${totalBatches}`;

        errors.push(message);
      }

      setImportProgress({
        sent: Math.min((i + 1) * IMPORT_BATCH_SIZE, items.length),
        total: items.length,
      });
    }

    const finalNotFoundCnpjs = Array.from(notFoundSet);
    const finalNotFoundCount =
      finalNotFoundCnpjs.length > 0 ? finalNotFoundCnpjs.length : notFoundCountAccumulator;

    setNotFoundSummary({
      count: finalNotFoundCount,
      cnpjs: finalNotFoundCnpjs,
    });

    setImportErrors(errors);
    setImporting(false);
    setImportDone(true);
    setImportProgress(null);

    if (finalNotFoundCount > 1) {
      setShowNotFoundModal(true);
    }
  };

  const isParsing = parseProgress !== null;

  const parsePercent = parseProgress
    ? parseProgress.phase === 'reading'
      ? 0
      : parseProgress.total > 0
        ? Math.round((parseProgress.processed / parseProgress.total) * 100)
        : 0
    : 0;

  const importPercent = importProgress
    ? importProgress.total > 0
      ? Math.round((importProgress.sent / importProgress.total) * 100)
      : 0
    : 0;

  const importableCount = parseResult?.validCount ?? 0;

  if (!isOpen) return null;

  return (
    <>
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Importar planilha de tamanhos"
      >
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
        />

        <div
          className={`relative bg-white rounded-2xl shadow-2xl w-[95vw] max-w-4xl max-h-[90vh] flex flex-col transition-all duration-300 ${
            visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
          }`}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Importar Tamanhos</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Faça upload de um arquivo XLSX com os tamanhos de loja
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={importing}
              className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-800 space-y-2">
                  <p className="font-medium">Padrão da planilha esperado:</p>
                  <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-blue-100/50">
                          <th className="px-3 py-2 text-left font-semibold text-blue-900">CNPJLojista</th>
                          <th className="px-3 py-2 text-left font-semibold text-blue-900">Tamanho de Loja</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-blue-100">
                          <td className="px-3 py-1.5 text-blue-700 font-mono">12345678000199</td>
                          <td className="px-3 py-1.5 text-blue-700">Numérica A</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p>
                    Tamanhos válidos: <strong>Não Informado</strong>, <strong>Numérica A</strong>, <strong>Numérica B</strong>, <strong>Numérica C</strong>, <strong>Outros</strong>, <strong>Ponderada Rede</strong>, <strong>Ponderada Independente</strong>, <strong>Atacado</strong>
                  </p>
                </div>
              </div>
            </div>

            {!file && !importing && !importDone && !isParsing && (
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => inputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer transition-all ${
                  isDragging
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
                }`}
                role="button"
                aria-label="Área de upload de arquivo"
              >
                <div
                  className={`p-3 rounded-xl mb-3 ${
                    isDragging ? 'bg-blue-100' : 'bg-gray-200'
                  }`}
                >
                  <Upload
                    className={`w-6 h-6 ${isDragging ? 'text-blue-600' : 'text-gray-500'}`}
                  />
                </div>
                <p className="text-sm font-medium text-gray-700">
                  {isDragging ? 'Solte o arquivo aqui' : 'Arraste ou clique para selecionar'}
                </p>
                <p className="text-xs text-gray-400 mt-1">Apenas arquivos .xlsx</p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleInputChange}
                  className="hidden"
                  aria-hidden="true"
                />
              </div>
            )}

            {isParsing && (
              <div className="py-8">
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                  <div className="w-full max-w-md">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-700">
                        {parseProgress?.phase === 'reading'
                          ? 'Lendo arquivo...'
                          : 'Validando registros...'}
                      </p>
                      <span className="text-sm font-semibold text-blue-600">
                        {parsePercent}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-blue-500 h-3 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${parsePercent}%` }}
                      />
                    </div>
                    {parseProgress && parseProgress.phase === 'validating' && (
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        {parseProgress.processed.toLocaleString('pt-BR')} de {parseProgress.total.toLocaleString('pt-BR')} linhas processadas
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {parseError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-700">{parseError}</p>
              </div>
            )}

            {file && parseResult && !importDone && (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                      {file.name}
                    </span>
                    <span className="text-xs text-gray-400">
                      ({parseResult.totalRows.toLocaleString('pt-BR')} linhas)
                    </span>
                  </div>
                  {!importing && (
                    <button
                      onClick={resetState}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                      Remover
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <div>
                      <p className="text-base font-bold text-emerald-700 leading-tight">
                        {parseResult.validCount.toLocaleString('pt-BR')}
                      </p>
                      <p className="text-[10px] text-emerald-600">Válidos</p>
                    </div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2.5">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <div>
                      <p className="text-base font-bold text-red-700 leading-tight">
                        {parseResult.invalidCount.toLocaleString('pt-BR')}
                      </p>
                      <p className="text-[10px] text-red-600">Inválidos</p>
                    </div>
                  </div>
                </div>

                {importing && importProgress && (
                  <div className="py-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                        <span className="text-sm font-medium text-gray-700">
                          Enviando registros...
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-blue-600">
                        {importPercent}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-blue-500 h-3 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${importPercent}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      {importProgress.sent.toLocaleString('pt-BR')} de {importProgress.total.toLocaleString('pt-BR')} registros enviados
                    </p>
                  </div>
                )}

                <ImportPreviewTable
                  totalRows={parseResult.totalRows}
                  validCount={parseResult.validCount}
                  invalidCount={parseResult.invalidCount}
                  tamanhoSummary={parseResult.tamanhoSummary}
                  errorSummary={parseResult.errorSummary}
                />
              </>
            )}

            {importDone && (
              <div className="flex flex-col items-center justify-center py-8 w-full">
                <div className={`rounded-full p-4 mb-4 ${importErrors.length > 0 ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                  {importErrors.length > 0 ? (
                    <AlertCircle className="w-8 h-8 text-amber-600" />
                  ) : (
                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                  )}
                </div>

                <p className="text-base font-semibold text-gray-900">
                  {importErrors.length > 0 ? 'Importação concluída com erros' : 'Importação concluída'}
                </p>

                <p className="text-sm text-gray-500 mt-1 text-center">
                  {importableCount.toLocaleString('pt-BR')} {importableCount === 1 ? 'registro processado' : 'registros processados'}
                </p>

                <div className="mt-4 w-full max-w-lg grid gap-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider">
                      Resumo de não encontrados
                    </p>
                    <p className="mt-1 text-sm text-blue-900 font-medium">
                      {notFoundSummary.count.toLocaleString('pt-BR')} CNPJs não encontrados
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      Esses CNPJs não foram cadastrados por não serem encontrados no sistema.
                    </p>

                    {notFoundSummary.cnpjs.length > 0 && (
                      <button
                        onClick={downloadNotFoundSpreadsheet}
                        className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Baixar planilha dos não processados
                      </button>
                    )}
                  </div>

                  {notFoundSummary.cnpjs.length > 0 && (
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                        <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          CNPJs não encontrados
                        </p>
                      </div>

                      <div className="max-h-[240px] overflow-y-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-white sticky top-0 z-10 border-b border-gray-200">
                            <tr>
                              <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase tracking-wider">
                                #
                              </th>
                              <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase tracking-wider">
                                CNPJ
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {notFoundSummary.cnpjs.map((cnpj, index) => (
                              <tr key={`${cnpj}-${index}`} className="hover:bg-gray-50">
                                <td className="px-4 py-2.5 text-gray-500">{index + 1}</td>
                                <td className="px-4 py-2.5 font-mono text-gray-800">{cnpj}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {importErrors.length > 0 && (
                    <div className="w-full bg-red-50 border border-red-200 rounded-xl p-3 max-h-[120px] overflow-y-auto">
                      {importErrors.map((err, i) => (
                        <p key={i} className="text-xs text-red-700">{err}</p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <span className="text-xs text-gray-400">
              {!importing && !isParsing && !showNotFoundModal && 'Pressione ESC para fechar'}
            </span>
            <div className="flex items-center gap-3">
              {!importDone && !isParsing && (
                <button
                  onClick={handleClose}
                  disabled={importing}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
              )}

              {file && parseResult && !importDone && (
                <button
                  onClick={handleImport}
                  disabled={importableCount === 0 || importing}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-blue-600/20 disabled:shadow-none"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Importando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Importar {importableCount.toLocaleString('pt-BR')} {importableCount === 1 ? 'registro' : 'registros'}
                    </>
                  )}
                </button>
              )}

              {importDone && (
                <button
                  onClick={handleClose}
                  className="px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-all shadow-sm shadow-blue-600/20"
                >
                  Fechar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showNotFoundModal && notFoundSummary.count > 1 && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 max-h-[85vh] flex flex-col">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="bg-amber-100 p-2 rounded-xl">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    CNPJs não encontrados
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {notFoundSummary.count.toLocaleString('pt-BR')} CNPJs não foram cadastrados por não serem encontrados no sistema.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowNotFoundModal(false)}
                className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Fechar aviso de CNPJs não encontrados"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-5 min-h-0 flex-1 overflow-hidden">
              <div className="border border-gray-200 rounded-xl overflow-hidden h-full flex flex-col">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Lista de CNPJs não processados
                  </p>
                </div>

                <div className="overflow-y-auto max-h-[40vh]">
                  <table className="min-w-full text-sm">
                    <thead className="bg-white sticky top-0 z-10 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase tracking-wider">
                          #
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-500 uppercase tracking-wider">
                          CNPJ
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {notFoundSummary.cnpjs.map((cnpj, index) => (
                        <tr key={`${cnpj}-${index}`} className="hover:bg-amber-50/40">
                          <td className="px-4 py-2.5 text-gray-500">
                            {index + 1}
                          </td>
                          <td className="px-4 py-2.5 font-mono text-gray-800">
                            {cnpj}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={downloadNotFoundSpreadsheet}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-xl text-sm font-medium hover:bg-amber-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Baixar planilha
              </button>

              <button
                onClick={() => setShowNotFoundModal(false)}
                className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};